import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { setCanonical, setMeta } from '@/utils/seo';
import { INDUSTRIES } from '@/data/industries';
import LeadForm from '@/components/LeadForm';
import { ServiceSchema, FAQSchema, HowToSchema, SpeakableSchema, BreadcrumbSchema } from '@/components/SchemaMarkup';
import { MapPin } from 'lucide-react';
import HeroBackground from '@/components/HeroBackground';
import api from '@/utils/api';

export default function StatePage() {
  const [cities, setCities] = useState([]);

  useEffect(() => {
    setCanonical('/workers-comp-insurance-california');
    setMeta('Workers Comp Insurance California | Compare Quotes | CompareWorkersComp.com',
      'Compare workers compensation insurance quotes for California businesses. All 482 cities, 6 industries, 50+ carriers.');
    api.get('/cities?limit=500').then(r => setCities(r.data.cities || [])).catch(() => {});
  }, []);

  return (
    <>
      <ServiceSchema name="Workers Comp Insurance California" description="Compare workers comp quotes for all California businesses" areaServed="California" />
      <HowToSchema />
      <SpeakableSchema name="Workers Comp Insurance California" url="https://compareworkerscomp.com/workers-comp-insurance-california" />
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'California' }]} />

      <section className="relative pt-24 pb-16 overflow-hidden">
        <HeroBackground />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="speakable-intro text-white/30 text-xs mb-3">Workers compensation insurance requirements and coverage information for California employers. Compare quotes from over 50 carriers statewide.</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-6 hero-text-shadow" data-testid="state-h1">Workers Comp Insurance in <span className="text-red-500">California</span></h1>
              <p className="text-white/80 text-lg mb-6 hero-text-shadow-sm">California requires workers compensation for every employer with 1+ employee. Compare quotes from 50+ carriers across all 482 cities.</p>
            </div>
            <LeadForm source="state-page" />
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-cwc-navy mb-8">Browse by Industry</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {INDUSTRIES.map(i => (
              <Link key={i.slug} to={`/workers-comp-for-${i.slug}-companies`} className="bg-gray-50 rounded-xl p-5 hover:bg-cwc-gold/5 transition-colors border border-gray-100">
                <i.Icon className="w-8 h-8 mb-3" style={{ color: i.color }} />
                <h3 className="font-bold text-cwc-navy mb-1">{i.name}</h3>
                <p className="text-gray-500 text-sm">{i.avgCost}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Cities */}
      <section className="bg-gray-50 py-16" data-testid="state-cities">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-cwc-navy mb-8">All California Cities ({cities.length})</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {cities.map(c => (
              <Link key={c.slug} to={`/workers-comp-insurance-${c.slug}-ca`} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-cwc-gold px-2 py-1.5 rounded hover:bg-white transition-colors">
                <MapPin className="w-3 h-3 shrink-0" /> {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
